import React, { useMemo, useState } from 'react';

import classNames from 'classnames';

import { money, stageStyleFor } from './overviewHelpers';
import StageProcurementsList from './StageProcurementsList';

/**
 * IB-699 — 7-stage pipeline board. Each stage tile shows a status dot, the stage name,
 * the count and summed value of procurements in that stage, and a fill bar (relative to
 * the busiest stage). Empty stages render a zero state, not a missing column. Clicking a
 * tile lists that stage's procurements inline below the board.
 */
const PipelineBoard = ({ stats, currencySymbol }) => {
    const byStatus = stats?.by_status ?? [];
    const [selectedStage, setSelectedStage] = useState(null);

    const maxValue = useMemo(() => Math.max(1, ...byStatus.map((stage) => Number(stage.value) || 0)), [byStatus]);

    const toggleStage = (stage) => {
        setSelectedStage((current) => (current?.status_id === stage.status_id && current?.status_name === stage.status_name ? null : stage));
    };

    return (
        <div className="tw-rounded-lg tw-bg-white tw-shadow-primary tw-px-6 tw-py-6.5">
            <div className="tw-flex tw-items-baseline tw-gap-2 tw-mb-4">
                <h4 className="tw-text-base tw-font-semibold tw-text-charcoal">{$t('Procurement pipeline')}</h4>
                <span className="tw-text-sm tw-text-charcoal-50">{$t('select a stage to list its procurements')}</span>
            </div>

            <div className="tw-grid tw-grid-cols-2 sm:tw-grid-cols-3 lg:tw-grid-cols-7 tw-gap-2">
                {byStatus.map((stage, index) => {
                    const style = stageStyleFor(index);
                    const isSelected = selectedStage?.status_name === stage.status_name;
                    const fillPercent = Math.round(((Number(stage.value) || 0) / maxValue) * 100);

                    return (
                        <button
                            key={stage.status_name}
                            type="button"
                            onClick={() => toggleStage(stage)}
                            className={classNames(
                                'tw-flex tw-flex-col tw-gap-2 tw-rounded-lg tw-border-2 tw-p-3 tw-text-start tw-transition-colors',
                                isSelected ? 'tw-border-blue' : 'tw-border-grey hover:tw-border-blue-25',
                            )}
                        >
                            <div className="tw-flex tw-items-center tw-gap-1.5">
                                <span className={classNames('tw-h-2 tw-w-2 tw-rounded-full tw-shrink-0', style.dot)} />
                                <span className="tw-text-xs tw-text-charcoal-75 tw-truncate">{stage.status_name}</span>
                            </div>
                            <div className="tw-text-2xl tw-font-semibold tw-text-charcoal tw-leading-none">{stage.count}</div>
                            <div className="tw-text-sm tw-text-charcoal-50">{money(currencySymbol, stage.value)}</div>
                            <div className="tw-h-1 tw-w-full tw-rounded-full tw-bg-grey tw-overflow-hidden">
                                <div className={classNames('tw-h-full tw-rounded-full', style.bar)} style={{ width: `${fillPercent}%` }} />
                            </div>
                        </button>
                    );
                })}
            </div>

            <p className="tw-mt-4 tw-text-sm tw-text-charcoal-50">
                {$t(
                    'Outstanding = the four pre-award stages. Completed procurements sit under Awarded, In progress and Complete — click any tile to list them.',
                )}
            </p>

            {selectedStage && (
                <div className="tw-mt-4">
                    <StageProcurementsList stage={selectedStage} currencySymbol={currencySymbol} onClose={() => setSelectedStage(null)} />
                </div>
            )}
        </div>
    );
};

export default PipelineBoard;
